<template>
    <div class="calc-string-input">
        <input
            v-if="locked"
            :class="['calc-input', { 'calc-input--invalid': invalid, 'calc-input--locked': locked }]"
            :value="modelValue"
            :maxlength="maxlength"
            readonly
        />
        <InputText
            v-else
            :model-value="displayValue"
            :class="['calc-input', { 'calc-input--invalid': invalid, 'calc-input--locked': locked }]"
            :placeholder="translatedPlaceholder"
            :inputmode="inputMode"
            :maxlength="maxlength"
            @update:model-value="onInput"
            @blur="onBlur"
        />
    </div>
</template>

<script>
import InputText from 'primevue/inputtext'

export default {
    name: 'CalcStringInput',

    components: { InputText },

    props: {
        modelValue: {
            type: [String, Number],
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        inputMode: {
            type: String,
            default: 'text',
        },
        locked: {
            type: Boolean,
            default: false,
        },
        formatter: {
            type: Function,
            default: null,
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        maxlength: {
            type: Number,
            default: null,
        },
    },

    emits: ['update:modelValue'],

    data() {
        return {
            // Local mirror used for the InputText's v-model so the
            // formatted display (e.g. "25.000 €") survives even when
            // the parent stores the canonical numeric value (25000).
            displayValue: this.toDisplay(this.modelValue),
        }
    },

    watch: {
        modelValue(next) {
            // When the parent (or programmatic reset) sets a new value,
            // only refresh displayValue if it doesn't already parse back
            // to the same value — this preserves in-progress user typing
            // and the formatted display after blur.
            const ourParsed = this.parseDisplay(this.displayValue)
            if (this.valuesEqual(ourParsed, next)) return
            this.displayValue = this.toDisplay(next)
        },
    },

    created() {
        this.applyFormatter()
    },

    computed: {
        translatedPlaceholder() {
            if (!this.placeholder) return ''
            if (this.placeholder.startsWith('§')) return this.$t(this.placeholder)
            return this.placeholder
        },
    },

    methods: {
        toDisplay(v) {
            if (v == null || v === '') return ''
            return String(v)
        },

        parseDisplay(v) {
            // The parent's onAmount/onUtilization handlers do the
            // canonical parse via parseLocalizedNumber. Here we just
            // compare strings/numbers for equality check.
            return v
        },

        valuesEqual(a, b) {
            if (a === b) return true
            if (a == null && (b === '' || b == null)) return true
            if (b == null && (a === '' || a == null)) return true
            return false
        },

        onInput(v) {
            this.displayValue = v
            this.$emit('update:modelValue', v)
        },

        applyFormatter() {
            if (!this.formatter || this.displayValue === '' || this.displayValue == null) return
            if (this.invalid) return
            const formatted = this.formatter(this.displayValue)
            if (formatted === '') return
            // Replace the local display only — do NOT re-emit. The
            // parent already has the canonical numeric value because
            // onInput emitted the raw keystroke; the formatter is for
            // display polish only.
            this.displayValue = formatted
        },

        onBlur() {
            this.applyFormatter()
        },
    },
}
</script>

<style scoped>
.calc-string-input {
    width: 100%;
}

.calc-string-input :deep(.calc-input),
.calc-string-input .calc-input {
    height: 48px;
    border-radius: 8px;
    background: white;
    border: 1px solid var(--calc-line);
    padding: 0 16px;
    font-family: 'TWK Lausanne', 'Inter', sans-serif;
    font-size: 18px;
    line-height: 28px;
    letter-spacing: -0.02em;
    color: var(--calc-ink);
    outline: none;
    transition:
        border-color var(--calc-t-fast),
        box-shadow var(--calc-t-fast);
    width: 100%;
}

.calc-string-input :deep(.calc-input)::placeholder,
.calc-string-input .calc-input::placeholder {
    color: var(--calc-ink-30);
}

.calc-string-input :deep(.calc-input:hover),
.calc-string-input .calc-input:hover {
    border-color: var(--calc-line);
}

.calc-string-input :deep(.calc-input:focus),
.calc-string-input .calc-input:focus {
    border-color: var(--calc-line);
    box-shadow: 0 0 0 1px var(--calc-line);
}

.calc-string-input :deep(.calc-input:focus-visible),
.calc-string-input .calc-input:focus-visible {
    outline: none;
}

.calc-string-input .calc-input--locked {
    background: var(--calc-bg-soft);
    color: var(--calc-ink-30);
    pointer-events: none;
}

.calc-string-input .calc-input--locked:hover,
.calc-string-input .calc-input--locked:focus,
.calc-string-input .calc-input--locked:focus-visible {
    border-color: var(--calc-line);
    box-shadow: none;
    outline: none;
}

.calc-string-input :deep(.calc-input--invalid),
.calc-string-input .calc-input--invalid {
    border-color: var(--calc-error, #d23030);
}

.calc-string-input :deep(.calc-input--invalid:focus),
.calc-string-input .calc-input--invalid:focus {
    border-color: var(--calc-error, #d23030);
    box-shadow: 0 0 0 1px var(--calc-error, #d23030);
}
</style>
